import React, { useState, useRef, useEffect } from "react";
import { SlIcon } from "@shoelace-style/shoelace/dist/react";
import { alphaformatDateTime, formatDate } from "../../../utils/dateFormat.js";
import { ConfirmationModal } from "../../../shared/ConfirmationModal.js";
import ContentWriterAssignmentModal from "../components/ContentWriterAssignmentModal.js";
import ContentDetailsCell from "../components/ContentDetailsCell.js";
import GoLiveButton from "../components/GoLiveButton.js";
import CreativeName from "../components/CreativeName.js";
import OrderDetails from "../components/OrderDetails.js";
import LocationDetails from "../components/LocationDetails.js";
import { EmitEvent } from "../../../services/socket.js";
import Select from "react-select";
import { LINE_ITEM_STATUS, useTableState } from "./useTableState.js";
import MessageIcon from "../components/MessageIcon.js";
import { AD_FORMAT_OPTIONS } from "./adFormats.js";

export const renderSalesPersonCell = (value, row, actionHandlers) => {
  const name = value?.sales_person_name;
  const hasDates = row.creative_start_date && row.creative_start_date !== -1;
  let dateRange = "";
  if (hasDates) {
    const start = alphaformatDateTime(row.creative_start_date);
    const end = alphaformatDateTime(row.creative_start_date, "end");
    dateRange = `${start.dateStr} ${start.timeStr} - ${end.dateStr} ${end.timeStr}`;
  }
  return (
    <div
      className="flex flex-col items-start min-w-0 cursor-pointer rounded p-1 transition-colors"
      title="Click to open chat"
    >
      <span className="flex gap-1 items-center font-semibold text-gray-800">
        {name}
        <MessageIcon row={row} onOpenChat={actionHandlers?.openChat} />
      </span>
      {value?.mobile && (
        <span className="flex gap-1 items-center text-gray-500 text-sm">
          <SlIcon library="lucide" name="phone" style={{ fontSize: "12px" }}></SlIcon>
          {value.mobile}
        </span>
      )}
      {hasDates && (
        <span className="text-xs text-gray-400 mt-0.5 whitespace-nowrap">{dateRange}</span>
      )}
    </div>
  );
};

export const renderDateTimeCell = (value) => {
  if (!value || value === -1) return "-";
  const { dateStr, timeStr } = alphaformatDateTime(value);
  return <span>{dateStr + " · " + timeStr}</span>;
};
const AdTypeSelector = ({row}) => {

  const [selectedAdType, setSelectedAdType] = useState(row?.ad_creative_sub_type);
  const [editAdType,setEditAdType] = useState(false)
  const {adTypeChange} = useTableState();


  const handleAdTypeChange = async () => {
    const res = await adTypeChange(row?.creative_id, selectedAdType);
    if(res.status==='SUCCESS' ){
      EmitEvent(row?.creative_id,res.data?.log[0])
    }
    setEditAdType(false);
  }

  return editAdType ? (
    <div className="flex flex-col items-center justify-center gap-2 w-full">
      <Select
        options={AD_FORMAT_OPTIONS}
        value={AD_FORMAT_OPTIONS.find(option => option.value === selectedAdType)}
        onChange={(option) => setSelectedAdType(option.value)}
        className="w-full"
      />
      <div className="flex items-center justify-center gap-2">
        <button className="px-2 py-1 bg-green-500 text-white rounded" onClick={handleAdTypeChange}>save</button>
        <button className="px-2 py-1 bg-red-500 text-white rounded" onClick={() => {setSelectedAdType(row.ad_creative_sub_type); setEditAdType(false)}}>cancel</button>
      </div>
    </div>
  ) : (
    <div className="flex items-center gap-1">
      {row?.ad_creative_sub_type ? <span className="dz-chip">{row?.ad_creative_sub_type}</span>:null}
      {+row?.status < 8 ? <sl-tooltip content="Edit Ad Type">
          <sl-icon-button library="lucide" name="edit" onClick={() => setEditAdType(true)}></sl-icon-button>
        </sl-tooltip>:null}
    </div>
  )

}
const AssignToCell = ({ row, isPending, designers, handleDesignerChange }) => {
  const [editing, setEditing] = useState(false);
  const [selectedDesigner, setSelectedDesigner] = useState(-1);
  const [showModal, setShowModal] = useState(false);
  const [pendingDesigner, setPendingDesigner] = useState(null);
  const cellRef = useRef(null);

  useEffect(() => {
    if (!editing) return;
    function handleClickOutside(event) {
      if (cellRef.current && !cellRef.current.contains(event.target)) {
        setEditing(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [editing]);

  const handleSelectChange = (e) => {
    const newDesignerId = e.target.value;
    const selectedDesignerObj = designers?.find((d) => d?.designer_id === parseInt(newDesignerId));
    if (!newDesignerId) return;
    setPendingDesigner({ id: newDesignerId, name: selectedDesignerObj?.name, obj: selectedDesignerObj });
    setShowModal(true);
  };

  const handleModalConfirm = () => {
    if (pendingDesigner) {
      setSelectedDesigner(pendingDesigner.id);
      const res = handleDesignerChange(row?.creative_id, pendingDesigner.id, pendingDesigner.name, row?.creative_name); 
      if(res.data?.log?.length){
         EmitEvent(row?.creative_id,res?.data?.log[0])
      }
    }
    setShowModal(false);
    setEditing(false);
    setPendingDesigner(null);
  };

  const handleModalCancel = () => {
    setShowModal(false);
    setEditing(false);
    setPendingDesigner(null);
  };

  return (
    <div ref={cellRef} className="flex items-center gap-2">
      {isPending && !editing ? (
        <sl-tooltip content="Edit Designer">
          <sl-icon-button library="lucide" name="edit" onClick={() => setEditing(true)}></sl-icon-button>
        </sl-tooltip>
      ) : null}

      {editing ? (
        <>
          <select className="border p-1 rounded" value={selectedDesigner} onChange={handleSelectChange}>
            <option value="">Select Designer</option>
            {Array.isArray(designers) && designers?.length > 0 ? (
              designers.map((designer) => (
                <option key={designer?.designer_id} value={designer?.designer_id}>
                  {designer?.name}
                </option>
              ))
            ) : (
              <option value="" disabled>
                No designers
              </option>
            )}
          </select>
          <ConfirmationModal open={showModal} message={`Are you sure you want to assign to ${pendingDesigner?.name}?`} onConfirm={handleModalConfirm} onCancel={handleModalCancel} />
        </>
      ) : (
        row?.designer_name ? (
          <span>{row.designer_name}</span>
        ) : (
          <span className="text-gray-400">Unassigned</span>
        )
      )}
    </div>
  );
};

export const renderAssignToCell = (row, designers, isPending, handleDesignerChange) => {
  return (
    <div>
      <AssignToCell row={row} isPending={isPending} designers={designers} handleDesignerChange={handleDesignerChange} />
    </div>
  );
};

const AssignContentWriterCell = ({ row, isPending, contentWriters, handleContentWriterChange }) => {
  const [showModal, setShowModal] = useState(false);
  const [currentContentWriter, setCurrentContentWriter] = useState(null);
  const handleAssignClick = () => {
    setCurrentContentWriter(
      row.content_writer_name
        ? {
            content_writer_id: row?.content_writer_id,
            name: row?.content_writer_name,
          }
        : null
    );
    setShowModal(true);
  };

  const handleModalConfirm = async (data) => {
    try {
      const res = await handleContentWriterChange(data.content_id, data.creative_id, data.content_writer_id, data.content_writer_name, data.description);
    } catch (error) {
      console.error("Error in content writer assignment:", error);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <button onClick={handleAssignClick} className="py-1 mx-1 flex items-center justify-center gap-2">
        <SlIcon library="lucide" name="edit-3" />
        {row?.content_writer_name ? row?.content_writer_name : "Assign Content Writer"}
      </button>

      <ContentWriterAssignmentModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleModalConfirm}
        contentWriters={contentWriters}
        currentContentWriter={currentContentWriter}
        currentDescription={ row.rough_contest_description || ""}
        creativeId={row.creative_id}
        content_id={row.content_id}
      />
    </div>
  );
};

export const renderAssignContentWriterCell = (row, contentWriters, isPending, handleContentWriterChange) => {
  return (
    <div className="flex flex-col items-start justify-start gap-2">
      <ContentDetailsCell creative_id={row?.creative_id} adDetails={row} />
      <AssignContentWriterCell row={row} isPending={isPending} contentWriters={contentWriters} handleContentWriterChange={handleContentWriterChange} />
    </div>
  );
};

export const renderPaymentStatusCell = (value) => <span className={`px-2 py-1  rounded-full  font-medium ${value === "Done" ? "bg-done text-done_text" : "bg-pending text-pending_text"}`}>{value}</span>;

export const renderLiveStatusCell = (row, handleGoToLive, goLiveLoadingIdx, rowIdx, actionHandlers) => {
  return <GoLiveButton row={row} handleGoToLive={handleGoToLive} goLiveLoadingIdx={goLiveLoadingIdx} rowIdx={rowIdx} actionHandlers={actionHandlers} />;
};

const renderContent = (creative_id, fetchContent, row) => {
  return <ContentDetailsCell creative_id={creative_id} adDetails={row} />;
};
const renderLineItemStatus = (row) => {
    if (!row?.line_item_status) return null;
    const item = LINE_ITEM_STATUS.find((item) => item.value === row?.line_item_status);
    if (!item) return null;
    return (
        <div className="flex gap-2 items-center justify-center">
            {item.icon && <img src={item.icon} alt={item.label} className="w-4 h-4" />}
            {item.label && <span>{item.label}</span>}
        </div>
    );
};
// Accept/Reject actions now live inside the chat (AdTypeRequestBanner). The table
// only surfaces that a request is pending; CST resolves it from the creative's chat.
const PendingRequestCell = ({color,label,row}) =>{
   return (
    <div className="flex flex-col items-center gap-1 w-full justify-center">
      <span
        title={row?.pending_log?.message || 'no description found'}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-medium whitespace-nowrap ${color}`}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
        {label}
      </span>
      <span className="inline-flex items-center gap-0.5 text-[10px] text-gray-500 whitespace-nowrap">
        <SlIcon library="lucide" name="message-square" style={{ fontSize: "11px" }} /> Resolve in chat
      </span>
    </div>
   );
}
export const renderDesignStatusCell = (row,statusCode) => {
  let color = "bg-gray-100 text-gray-800";
  let label = "Unknown";

  switch (statusCode) {
    case -1:
      label = "Pending Request";
      color = "bg-pending text-pending_text";
      break;
    case 1:
      label = "Draft";
      color = "bg-gray-100 text-gray-800";
      break;
    case 2:
      label = "Saved";
      color = "bg-blue-100 text-blue-800";
      break;
    case 3:
      label = "Picked";
      color = "bg-pending text-pending_text";
      break;
    case 4:
      label = "Completed";
      color = "bg-done text-done_text";
      break;
    case 5:
      label = "Rejected";
      color = "bg-reject text-reject_text";
      break;
    case 6:
      label = "Accepted";
      color = "bg-client_approve text-client_approve_text";
      break;
    case 7:
      label = row?.paylater_due_date ? `paylater-${formatDate(row?.paylater_due_date)}` : "Payment Success";
      color = row?.paylater_due_date ? 'bg-done text-orange-800' : "bg-green-100 text-green-800";
      break;
    case 8:
      label = "Order Created";
      color = "bg-done text-done_text";
      break;
    case 9:
      label = "Completed";
      color = "bg-not_pickup text-not_pickup_text";
      break;
    default:
      label = "Unknown";
      color = "bg-gray-100 text-gray-800";
  }
  return statusCode === -1 ? 
    <PendingRequestCell color={color} label={label} row={row}/>
   :(
    <div className="flex items-center gap-2 w-full justify-center">
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-medium ${color}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
        {label}
      </span>
    </div>
  );
};

export const renderAdType = (row) => {
  return (
    <div className="flex flex-col gap-1 justify-start items-start">
      {row?.type && <span className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{row.type}</span>}
      <AdTypeSelector row={row} />
    </div>
  );
};
export const renderOrderDetails = (row) => {
  if(row?.status !== 8) return null;
  return <OrderDetails creative_id={row?.creative_id} campaign_id={row?.campaign_id} creative_name={row?.creative_name} />;
};
export const renderLocationDetails = (row) => {
  return <LocationDetails data={row} />;
};
export const renderDefaultCell = (value) => <span className="">{value}</span>;

export const createCellRenderer = () => {
  return (key, value, row, designers, contentWriters, isPending, handleDesignerChange, handleContentWriterChange, handleGoToLive, goLiveLoadingIdx, rowIdx, fetchContent, actionHandlers) => {
    switch (key) {
      case "sales_person":
        return renderSalesPersonCell(value, row, actionHandlers);
      case "designer_name":
        return renderAssignToCell(row, designers, isPending, handleDesignerChange);
      case "content_writer_name":
        return renderAssignContentWriterCell(row, contentWriters, isPending, handleContentWriterChange);
      case "paymentStatus":
        return renderPaymentStatusCell(value);
      case "live_status":
        return renderLiveStatusCell(row, handleGoToLive, goLiveLoadingIdx, rowIdx, actionHandlers);
      case "status":
        return renderDesignStatusCell(row,value);
      case "created_at":
        return renderDateTimeCell(value);
      case "creative_name":
        return <CreativeName row={row} />;
      case "ad_creative_sub_type":
        return renderAdType(row);
      case "content":
        return renderContent(row?.creative_id, fetchContent, row);
      case "order_details":
        return renderOrderDetails(row);
      case "location_details":
        return renderLocationDetails(row);
      case "live_item_status":
        return renderLineItemStatus(row);
      case "sno":
        return <span className="">{rowIdx+1}</span>;
      default:
        return renderDefaultCell(value);
    }
  };
};
